import clsx from 'clsx';
import React, { useState } from 'react';

export type SuggestionMenuItem<T = unknown> = {
  id: string;
} & T;

interface SuggestionMenuProps<T> {
  items: SuggestionMenuItem<T>[];
  onSelect: (item: SuggestionMenuItem<T>) => void;
  renderItem: (
    item: SuggestionMenuItem<T>,
    isFocused: boolean,
  ) => React.ReactNode;
}

export const SuggestionMenu = <T,>({
  items,
  onSelect,
  renderItem,
}: SuggestionMenuProps<T>): React.ReactElement | null => {
  const [focusedIndex, setFocusedIndex] = useState(0);

  // Reset focused index when items change
  React.useEffect(() => {
    setFocusedIndex(0);
  }, [items]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setFocusedIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        const selectedItem = items[focusedIndex];
        if (selectedItem) {
          onSelect(selectedItem);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () =>
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [focusedIndex, items, onSelect]);

  if (items.length === 0) {
    return null;
  }

  return (
    <ul
      className="flex flex-col list-none bg-white border rounded shadow-popover-050"
      role="menu"
    >
      {items.map((item, index) => (
        <li
          key={item.id}
          className={clsx(
            'flex flex-row items-center px-4 py-2 gap-3 cursor-pointer hover:bg-primary-hover rounded',
            focusedIndex === index && 'bg-primary-hover',
          )}
          onClick={() => onSelect(item)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onSelect(item);
            }
          }}
          tabIndex={index}
          role="menuitem"
        >
          {renderItem(item, focusedIndex === index)}
        </li>
      ))}
    </ul>
  );
};
